import { watch, onScopeDispose } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import DeliveryUnknownsAPI from 'dashboard/api/teletel/deliveryUnknowns';
import { getMetaDelivery } from './deliveryUnknown';

// A killed sender cannot push its terminal event. The deadline triggers only a
// bounded server read; the browser never decides that sending was uncertain.
export const useDeliveryStateRefresh = (
  delivery,
  messageId,
  conversationId
) => {
  const route = useRoute();
  const store = useStore();
  let timer;
  let generation = 0;
  let reads = 0;
  // `id` is also a folder identifier on custom-view routes. It denotes a
  // conversation only in the new inbox view, unlike the two explicit names.
  const routeConversationId = () =>
    route.params.conversation_id ??
    route.params.conversationId ??
    (route.name === 'inbox_view_conversation' ? route.params.id : undefined);
  const activeScope = () =>
    route.params.accountId &&
    routeConversationId() !== undefined &&
    String(routeConversationId()) === String(conversationId());
  const schedule = (delay, version) => {
    timer = setTimeout(async () => {
      if (version !== generation || !activeScope()) return;
      reads += 1;
      let keepReading = true;
      try {
        const { data } = await DeliveryUnknownsAPI.refreshMessage(
          conversationId(),
          messageId(),
          route.params.accountId
        );
        if (version !== generation || !activeScope()) return;
        const message = data.payload?.find(item => item.id === messageId());
        if (message) {
          keepReading =
            getMetaDelivery(
              message.additional_attributes,
              message.content_attributes
            )?.state === 'in_flight';
          await store.dispatch('addMessage', message);
        }
      } catch {
        // Reading can fail without changing the message's delivery state.
      }
      if (keepReading && version === generation && activeScope() && reads < 3) {
        schedule(10000, version);
      }
    }, delay);
  };
  watch(
    () => [
      delivery.value?.state,
      delivery.value?.attemptId,
      delivery.value?.deadlineAt,
      route.params.accountId,
      route.name,
      routeConversationId(),
      messageId(),
      conversationId(),
    ],
    () => {
      clearTimeout(timer);
      generation += 1;
      reads = 0;
      const deadline = Date.parse(delivery.value?.deadlineAt);
      if (
        delivery.value?.state === 'in_flight' &&
        Number.isFinite(deadline) &&
        activeScope()
      ) {
        schedule(Math.max(1000, deadline + 1000 - Date.now()), generation);
      }
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    generation += 1;
    clearTimeout(timer);
  });
};
